import type { ReactNode, JSX} from "react";
import { Row } from "./Row";
import { Stack } from "./Stack";
import { EditableTitle } from "./EditableTitle";
import { cn } from "./cn";

export interface TopBarHeaderProps {
  readonly leading?: ReactNode;
  readonly title: ReactNode;
  readonly subtitle?: ReactNode;
  readonly extra?: ReactNode;
  readonly actions?: ReactNode;
  readonly className?: string;
  readonly titleClassName?: string;
  readonly subtitleClassName?: string;
  /** When provided, title becomes editable (click to edit, Enter to save). */
  readonly onTitleEdit?: (name: string) => void;
}

/**
 * Unified top-bar header used across module detail views.
 * Layout: leading avatar/icon, title + subtitle, trailing actions.
 */
export function TopBarHeader({
  leading,
  title,
  subtitle,
  extra,
  actions,
  className,
  titleClassName,
  subtitleClassName,
  onTitleEdit,
}: TopBarHeaderProps): JSX.Element {
  // Editable + static titles must share the SAME typography — EditableTitle's
  // display span carries no weight of its own, so pass the header title style
  // explicitly (otherwise rename-enabled modules like contacts render a
  // thinner name than non-rename ones like companies).
  const titleClass = cn("text-[15px] font-semibold leading-tight text-content", titleClassName);
  const titleContent = onTitleEdit ? (
    <EditableTitle
      value={typeof title === "string" ? title : null}
      onCommit={onTitleEdit}
      className={titleClass}
    />
  ) : (
    <div className={cn("truncate", titleClass)}>{title}</div>
  );

  return (
    <Row align={extra ? "start" : "center"} justify="between" className={cn("h-full w-full", className)}>
      <Row gap={3} align={extra ? "start" : "center"} className="min-w-0 flex-1">
        {leading ? <div className={cn("shrink-0", extra ? "mt-0.5" : "")}>{leading}</div> : null}
        <Stack className="min-w-0">
          {titleContent}
          {subtitle ? (
            <div className={cn("truncate text-[11px] leading-tight text-content-secondary", subtitleClassName)}>
              {subtitle}
            </div>
          ) : null}
          {extra ?? null}
        </Stack>
      </Row>
      {actions ? <Row gap={2} className="shrink-0">{actions}</Row> : null}
    </Row>
  );
}
